import React from "react";
import { Link } from "react-router-dom";
import { LuCheck } from "react-icons/lu";
import BrandLogo from "../../common/BrandLogo";
import useCiT from "../../common/ci/i18n";
import { whatsappLink } from "../../config/contact";

/**
 * Auth screens ka common design (login, signup, OTP, forgot password).
 *
 * Design canvas "Colour A" ke rang hi: navy #182A70 side panel + safed card,
 * Barlow Condensed heading, 50px input, orange primary button.
 *
 * `side` = bada do-column card (login/signup ke liye).
 * `standalone` = page par site ka header nahi hai (OTP / forgot-password routes
 * App.tsx ki `routes` list me nahi hain) — tab upar apna chhota logo bar lagta hai.
 */

export const AUTH_INPUT_H = "h-[50px]";

export interface AuthFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  prefix?: string;
  hint?: React.ReactNode;
  error?: string;
}

export const AuthField: React.FC<AuthFieldProps> = ({
  label,
  icon,
  right,
  prefix,
  hint,
  error,
  className = "",
  ...rest
}) => (
  <label className="block">
    <span className="mb-1.5 flex items-center justify-between gap-3">
      <span className="text-[12px] font-bold uppercase tracking-[0.06em] text-ci-muted">
        {label}
      </span>
      {hint ? <span className="text-[12px] font-medium text-ci-muted">{hint}</span> : null}
    </span>
    <span
      className={`flex ${AUTH_INPUT_H} items-center gap-2 rounded-md border bg-white px-3 transition focus-within:ring-2 focus-within:ring-[#182A70]/15 ${
        error ? "border-[#c0392b]" : "border-[#cfc9bf] focus-within:border-ci-navy"
      }`}
    >
      {icon ? <span className="shrink-0 text-ci-navy">{icon}</span> : null}
      {prefix ? (
        <span className="ci-num shrink-0 border-r border-ci-line pr-2 text-[15px] font-semibold text-ci-muted">
          {prefix}
        </span>
      ) : null}
      <input
        className={`h-full w-full min-w-0 bg-transparent text-[15px] font-medium text-ci-ink outline-none placeholder:font-normal placeholder:text-[#9aa0a6] ${className}`}
        {...rest}
      />
      {right}
    </span>
    {error ? (
      <span className="mt-1 block text-[12.5px] font-medium text-[#c0392b]">{error}</span>
    ) : null}
  </label>
);

/** Password ke aankh wala button — har jagah ek jaisa. */
export const EyeToggle: React.FC<{ shown: boolean; onToggle: () => void }> = ({
  shown,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={shown ? "Password chhupayein" : "Password dikhayein"}
    className="shrink-0 rounded p-1 text-ci-muted transition hover:text-ci-navy"
  >
    {shown ? (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    ) : (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M2 12s3.6-6.5 10-6.5c1.9 0 3.5.6 4.8 1.4M22 12s-3.6 6.5-10 6.5c-1.9 0-3.5-.6-4.8-1.4"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path d="m4 4 16 16" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    )}
  </button>
);

/** Card ke andar ka heading block. */
export const AuthHeading: React.FC<{ title: string; sub?: React.ReactNode }> = ({
  title,
  sub,
}) => (
  <div className="mb-5">
    <h1 className="font-display text-[30px] font-bold uppercase leading-none text-ci-navy sm:text-[34px]">
      {title}
    </h1>
    {sub ? <p className="mt-2 text-[14px] leading-snug text-ci-muted">{sub}</p> : null}
  </div>
);

export const AuthTabs: React.FC<{
  active: "in" | "up";
  onChange: (next: "in" | "up") => void;
}> = ({ active, onChange }) => {
  const t = useCiT();
  const tab = (key: "in" | "up", label: string) => (
    <button
      type="button"
      onClick={() => onChange(key)}
      aria-current={active === key}
      className={`flex-1 border-b-2 pb-2.5 text-[16px] font-semibold transition ${
        active === key
          ? "border-ci-orange text-ci-navy"
          : "border-ci-line text-ci-muted hover:text-ci-navy"
      }`}
    >
      {t(label)}
    </button>
  );
  return (
    <div className="mb-6 flex gap-6">
      {tab("in", "Sign in")}
      {tab("up", "New account")}
    </div>
  );
};

const SidePanel: React.FC = () => {
  const t = useCiT();
  const points = [
    "Aaj ka rate — GST sahit, roz update",
    "Har order par GST bill",
    "Rudrapur godown se 1–2 din me delivery",
    "Dealer credit · godown pickup bhi",
  ];
  return (
    <div className="relative hidden w-[42%] shrink-0 flex-col justify-between overflow-hidden bg-ci-navy p-8 text-white lg:flex">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-ci-navy2"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-10 h-56 w-56 rounded-full bg-[#101c4d]"
      />
      <div className="relative">
        <BrandLogo variant="light" markSize={44} wordmarkWidth={172} />
        <h2 className="mt-9 font-display text-[36px] font-bold uppercase leading-[1.05]">
          {t("Rudrapur ka loha bazaar — ek login me")}
        </h2>
        <ul className="mt-6 flex flex-col gap-3">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-2.5 text-[14.5px] text-[#c9d2de]">
              <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-ci-orange text-white">
                <LuCheck size={12} strokeWidth={3} />
              </span>
              <span>{t(p)}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="relative mt-10 text-[13px] text-[#c9d2de]">
        {t("Rate dekhne ke liye login zaroori nahi — login sirf order ke liye.")}
        <a
          href={whatsappLink("Website login me dikkat aa rahi hai")}
          target="_blank"
          rel="noreferrer"
          className="mt-2 block font-semibold text-white underline underline-offset-2"
        >
          {t("Madad chahiye? WhatsApp karein")}
        </a>
      </div>
    </div>
  );
};

export const AuthShell: React.FC<{
  side?: boolean;
  standalone?: boolean;
  children?: React.ReactNode;
}> = ({ side = false, standalone = false, children }) => {
  const t = useCiT();
  return (
    <div className="w-full bg-ci-bg px-4 py-6 sm:py-10">
      {standalone ? (
        <div className="mx-auto mb-5 flex w-full max-w-[980px] items-center justify-between">
          <Link to="/" aria-label="Chawla Ispat">
            <BrandLogo markSize={38} wordmarkWidth={140} showTagline={false} />
          </Link>
          <Link
            to="/"
            className="text-[14px] font-semibold text-ci-navy underline-offset-2 hover:underline"
          >
            {t("Home")}
          </Link>
        </div>
      ) : null}
      <div
        className={`mx-auto flex w-full overflow-hidden rounded-xl border border-ci-line bg-white shadow-[0_12px_34px_rgba(21,23,26,0.08)] ${
          side ? "max-w-[980px]" : "max-w-[460px]"
        }`}
      >
        {side ? <SidePanel /> : null}
        <div className="w-full min-w-0 flex-1 p-5 sm:p-8">{children}</div>
      </div>
    </div>
  );
};

export default AuthShell;
